import { BuildClass, guid, Sleep } from '../../../../universal';
import { _, React } from '../../../lib';
import { Alerts } from '../../component-main';
import { ButtonGroup } from '../button-group';
import { Button } from '../buttons';
import { Checkbox } from '../checkbox';
import { PromptConfirm } from '../confirm';
import { FileUploadDropZone } from '../file-upload';
import { LoadingSpinnerSmall } from '../loading';
import { CJSX, useRSInstance } from '../meta-types';
import { stubDiv } from '../stubs';
import { TextareaWithVoice } from '../text-with-voice';
import { getChatCSSVars } from './styles';
import { ThreadMessage } from './thread-message';
import { ThreadTextBox } from './thread-textbox';
import { createEmptyInputState, } from './types';
const ChatThreadTurns = (props) => {
    const { rs, messages, thoughtAccess, theme, onDeleteMessage, onRerunMessage, toolHandlers, } = props;
    const latestAssistantMessageID = React.useMemo(() => messages
        .slice()
        .reverse()
        .find(msg => (msg.content[0]?.role ?? 'assistant') === 'assistant')?.id ??
        null, [messages]);
    return (React.createElement(React.Fragment, null, groupMessagesIntoTurns(messages).map((turn, turnIndex) => (React.createElement("div", { key: turn.userMessage?.id ?? `turn-${turnIndex}`, className: "mb-4" },
        turn.userMessage && (React.createElement(UserMessageInTurn, { message: turn.userMessage, rs: rs, thoughtAccess: thoughtAccess, onDeleteMessage: onDeleteMessage })),
        turn.responses.map(msg => (React.createElement("div", { key: msg.id, className: "mt-2" },
            React.createElement(ThreadMessage, { ...msg, className: rs.props.messageClassName?.(msg), bubbleClassName: rs.props.messageBubbleClassName?.(msg), metadataSlot: rs.props.messageMetadataSlot?.(msg), isExpanded: rs.state.expandedThinkingMessageID === msg.id, onToggleExpanded: () => {
                    rs.updateState(s => ({
                        expandedThinkingMessageID: s.expandedThinkingMessageID === msg.id
                            ? null
                            : msg.id,
                    }));
                }, collapseLevel: rs.state.messageCollapseLevels[msg.id] ??
                    (msg.isStreaming ? 1 : 0), onCollapseLevelChange: level => {
                    rs.updateState(s => ({
                        ...s,
                        messageCollapseLevels: {
                            ...s.messageCollapseLevels,
                            [msg.id]: level,
                        },
                    }));
                }, expandedItems: rs.state.messageExpandedItems[msg.id] ?? new Set(), onToggleItem: itemID => {
                    rs.updateState(s => {
                        const current = s.messageExpandedItems[msg.id] ?? new Set();
                        const next = new Set(current);
                        if (next.has(itemID)) {
                            next.delete(itemID);
                        }
                        else {
                            next.add(itemID);
                        }
                        return {
                            ...s,
                            messageExpandedItems: {
                                ...s.messageExpandedItems,
                                [msg.id]: next,
                            },
                        };
                    });
                }, toolRenderers: rs.props.toolRenderers, toolHandlers: toolHandlers, groupChat: rs.props.groupChat, participantNames: rs.props.participantNames, currentUserID: rs.props.currentUserID, thoughtAccess: thoughtAccess, theme: theme, onDelete: onDeleteMessage
                    ? () => {
                        onDeleteMessage(msg);
                    }
                    : undefined, onRerun: onRerunMessage && msg.id === latestAssistantMessageID
                    ? () => {
                        onRerunMessage(msg);
                    }
                    : undefined })))))))));
};
const ChatThreadStreamingAndChanges = (props) => {
    const { rs, pendingMessage, thoughtAccess, theme, showChangesTray, showQuestionsTray, showApprovedChanges, pendingChanges, pendingQuestions, changesTrayMode, questionsTrayMode, onChangesTrayModeChange, onQuestionsTrayModeChange, executingChangeIDs, isRunning, onQuestionTabChange, onApproveChanges, onRejectChanges, onSubmitQuestions, } = props;
    const threadFileLookup = React.useMemo(() => getThreadFileLookup(rs.props.controlled?.messages ?? rs.state.currentMessages, pendingMessage), [rs.props.controlled?.messages, rs.state.currentMessages, pendingMessage]);
    const toolHandlers = React.useMemo(() => ({
        ...(rs.props.toolHandlers ?? {}),
        getThreadFile: (fileID) => threadFileLookup[fileID] ?? null,
    }), [rs.props.toolHandlers, threadFileLookup]);
    return (React.createElement(React.Fragment, null,
        pendingMessage && (React.createElement("div", { className: "mt-2" },
            React.createElement(StreamingMessage, { pending: pendingMessage, isExpanded: rs.state.expandedThinkingMessageID === pendingMessage.id, onToggleExpanded: () => {
                    rs.updateState(s => ({
                        expandedThinkingMessageID: s.expandedThinkingMessageID === pendingMessage.id
                            ? null
                            : pendingMessage.id,
                    }));
                }, collapseLevel: rs.state.messageCollapseLevels[pendingMessage.id] ?? 1, onCollapseLevelChange: level => {
                    rs.updateState(s => ({
                        ...s,
                        messageCollapseLevels: {
                            ...s.messageCollapseLevels,
                            [pendingMessage.id]: level,
                        },
                    }));
                }, expandedItems: rs.state.messageExpandedItems[pendingMessage.id] ?? new Set(), onToggleItem: itemID => {
                    rs.updateState(s => {
                        const current = s.messageExpandedItems[pendingMessage.id] ?? new Set();
                        const next = new Set(current);
                        if (next.has(itemID)) {
                            next.delete(itemID);
                        }
                        else {
                            next.add(itemID);
                        }
                        return {
                            ...s,
                            messageExpandedItems: {
                                ...s.messageExpandedItems,
                                [pendingMessage.id]: next,
                            },
                        };
                    });
                }, toolRenderers: rs.props.toolRenderers, toolHandlers: toolHandlers, thoughtAccess: thoughtAccess, theme: theme }))),
        showChangesTray && rs.props.change && (React.createElement("div", { className: BuildClass({
                'mt-3 px-1': true,
                'w-full max-w-[800px] mx-auto': changesTrayMode === 'collapsed',
            }) },
            changesTrayMode === 'expanded' && (React.createElement("button", { type: "button", className: "mb-2 w-full rounded border border-[oklch(var(--chat-l-text)_0_0_/_0.12)] bg-[oklch(var(--chat-l-surface)_0_0_/_0.65)] px-3 py-1 text-left text-xs text-[oklch(var(--chat-l-text)_0_0_/_0.72)] hover:bg-[oklch(var(--chat-l-surface)_0_0_/_0.8)]", onClick: () => {
                    onChangesTrayModeChange('open');
                } }, "Showing pending changes only. Click to see message thread")),
            React.createElement(PendingChangesTray, { changes: pendingChanges, renderers: rs.props.change.renderers, toolHandlers: toolHandlers, onApprove: ids => {
                    onApproveChanges(ids).catch(console.error);
                }, onReject: onRejectChanges, executingIDs: executingChangeIDs, disabled: isRunning, widgetToGroupCode: rs.props.change.widgetToGroupCode ?? {}, showApprovedChanges: showApprovedChanges, displayMode: changesTrayMode, onDisplayModeChange: onChangesTrayModeChange }))),
        showQuestionsTray && (React.createElement("div", { className: "mt-3 px-1" },
            React.createElement(PendingQuestionsTray, { questions: pendingQuestions, onSubmit: onSubmitQuestions, disabled: isRunning, displayMode: questionsTrayMode, onActiveTabChange: onQuestionTabChange, onDisplayModeChange: onQuestionsTrayModeChange })))));
};
export const ChatThread = React.forwardRef((props, ref) => {
    const rs = useRSInstance({
        props,
        defaultState: getDefaultState,
        refs: {
            container: React.useRef(stubDiv),
            messageList: React.useRef(null),
            textarea: React.useRef(null),
            inputWrapper: React.useRef(null),
        },
        actionToDelta: null,
    });
    React.useImperativeHandle(ref, () => ({
        focusTextarea: () => rs.refs.textarea.current?.focus(),
    }));
    const userScrolledAwayFromBottomRef = React.useRef(false);
    const isControlled = Boolean(rs.props.controlled);
    const effectiveMessages = rs.props.controlled?.messages ?? rs.state.currentMessages;
    const effectivePendingMessage = rs.props.controlled?.pendingMessage ?? rs.state.pendingMessage;
    const effectiveIsRunning = rs.props.controlled?.isRunning ?? rs.state.isRunning;
    const effectiveThreadID = rs.props.controlled?.threadID ?? rs.state.threadID;
    const effectivePendingChanges = rs.props.controlled?.pendingChanges ?? rs.state.pendingChanges;
    const effectivePendingQuestions = rs.props.controlled?.pendingQuestions ?? rs.state.pendingQuestions;
    const threadFileLookup = React.useMemo(() => getThreadFileLookup(effectiveMessages, effectivePendingMessage), [effectiveMessages, effectivePendingMessage]);
    const toolHandlers = React.useMemo(() => ({
        ...(rs.props.toolHandlers ?? {}),
        getThreadFile: (fileID) => threadFileLookup[fileID] ?? null,
    }), [rs.props.toolHandlers, threadFileLookup]);
    React.useEffect(() => {
        if (isControlled) {
            return;
        }
        if (rs.state.isRunning) {
            return;
        }
        const propIDs = new Set(props.messages.map(m => m.id));
        const stateIDs = new Set(rs.state.currentMessages.map(m => m.id));
        const propsAreDifferent = propIDs.size !== stateIDs.size ||
            [...propIDs].some(id => !stateIDs.has(id));
        if (propsAreDifferent && props.messages.length > 0) {
            rs.updateState({ currentMessages: props.messages });
        }
    }, [props.messages, rs, rs.state.isRunning, isControlled]);
    const isAtBottom = (el, threshold = 20) => el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    const scrollToBottom = React.useCallback(() => {
        const el = rs.refs.messageList.current;
        if (el) {
            userScrolledAwayFromBottomRef.current = false;
            el.scrollTop = el.scrollHeight;
        }
    }, [rs]);
    const handleScrollIntent = React.useCallback((event) => {
        if (event.deltaY < 0 && event.currentTarget.scrollTop > 0) {
            userScrolledAwayFromBottomRef.current = true;
            if (rs.state.isScrollAnchored) {
                rs.updateState({ isScrollAnchored: false });
            }
        }
    }, [rs, rs.state.isScrollAnchored]);
    React.useEffect(() => {
        const el = rs.refs.messageList.current;
        if (!el) {
            return;
        }
        const handleScroll = () => {
            const anchored = isAtBottom(el);
            if (anchored) {
                userScrolledAwayFromBottomRef.current = false;
            }
            if (anchored !== rs.state.isScrollAnchored) {
                rs.updateState({ isScrollAnchored: anchored });
            }
        };
        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            el.removeEventListener('scroll', handleScroll);
        };
    }, [rs, rs.state.isScrollAnchored]);
    React.useEffect(() => {
        const el = rs.refs.messageList.current;
        if (!el) {
            return undefined;
        }
        if (rs.state.isScrollAnchored && !userScrolledAwayFromBottomRef.current) {
            scrollToBottom();
            return undefined;
        }
        const timeoutID = window.setTimeout(() => {
            const nowAnchored = isAtBottom(el);
            if (nowAnchored) {
                userScrolledAwayFromBottomRef.current = false;
            }
            if (nowAnchored !== rs.state.isScrollAnchored) {
                rs.updateState({ isScrollAnchored: nowAnchored });
            }
        }, 0);
        return () => {
            window.clearTimeout(timeoutID);
        };
    }, [
        rs,
        effectiveMessages,
        effectivePendingMessage,
        rs.state.expandedThinkingMessageID,
        scrollToBottom,
    ]);
    React.useEffect(() => {
        const inputEl = rs.refs.inputWrapper.current;
        if (!inputEl) {
            return;
        }
        const updateHeight = () => {
            const height = inputEl.offsetHeight;
            rs.updateState({ inputHeight: height });
            if (rs.state.isScrollAnchored && !userScrolledAwayFromBottomRef.current) {
                scrollToBottom();
            }
        };
        updateHeight();
        const observer = new ResizeObserver(() => {
            updateHeight();
        });
        observer.observe(inputEl);
        return () => {
            observer.disconnect();
        };
    }, [rs, rs.state.isScrollAnchored, scrollToBottom]);
    React.useEffect(() => {
        if (props.initialThreadID && props.initialThreadID !== rs.state.threadID) {
            rs.updateState({ threadID: props.initialThreadID });
        }
    }, [props.initialThreadID, rs]);
    const onContentChangeRef = React.useRef(rs.props.onContentChange);
    onContentChangeRef.current = rs.props.onContentChange;
    React.useEffect(() => {
        if (isControlled) {
            return;
        }
        onContentChangeRef.current?.({
            threadID: rs.state.threadID ?? null,
            messages: rs.state.currentMessages,
            pendingChanges: rs.state.pendingChanges,
            pendingQuestions: rs.state.pendingQuestions,
        });
    }, [
        rs.state.threadID,
        rs.state.currentMessages,
        rs.state.pendingChanges,
        rs.state.pendingQuestions,
        isControlled,
    ]);
    const hints = React.useMemo(() => effectiveMessages.length === 0 && !rs.props.messagesSlot
        ? rs.props.hintBubbles?.()
        : null, [effectiveMessages.length, rs.props]);
    const handleSend = (content, intelligence) => {
        if (content.length === 0) {
            return;
        }
        const userMessage = {
            id: guid(),
            content,
            timestamp: new Date().toISOString(),
            avatar: null,
        };
        rs.updateState({
            input: createEmptyInputState(intelligence),
            expandedThinkingMessageID: null,
            isScrollAnchored: true,
        });
        scrollToBottom();
        if (isControlled && rs.props.controlled) {
            rs.props.controlled.onSend({
                content,
                userMessage,
                intelligence,
            });
            return;
        }
        const updatedMessages = [...rs.state.currentMessages, userMessage];
        rs.updateState({
            isRunning: true,
            currentMessages: updatedMessages,
            pendingMessage: {
                id: guid(),
                content: [],
                startedInference: null,
                currentStatus: 'Starting...',
                intelligence,
            },
        });
        rs.props.sending?.onSend({
            threadID: rs.state.threadID ?? null,
            message: content,
            pendingChanges: rs.state.pendingChanges
                .filter(a => a.status === 'pending')
                .map(a => ({
                id: a.id,
                toolName: a.toolName,
                widget: a.widget,
                input: a.input,
                status: 'pending',
            })),
            pendingQuestions: rs.state.pendingQuestions
                .filter(question => question.status === 'pending')
                .map(question => ({
                id: question.id,
                toolName: question.toolName,
                title: question.title,
                question: question.question,
                multiple: question.multiple,
                options: question.options,
                status: 'pending',
            })),
            intelligence,
            onUpdate: response => {
                rs.updateState({
                    pendingMessage: {
                        id: response.id,
                        content: response.content,
                        startedInference: response.metrics?.timers?.startedInference ?? null,
                        currentStatus: response.currentStatus,
                        intelligence: response.intelligence,
                    },
                });
            },
            onComplete: response => {
                if (response === null) {
                    rs.updateState({
                        isRunning: false,
                        pendingMessage: null,
                    });
                    return;
                }
                const newMessage = _.isString(response)
                    ? {
                        id: guid(),
                        content: [
                            {
                                role: 'assistant',
                                timestamp: new Date().toISOString(),
                                type: 'error',
                                error: response,
                            },
                        ],
                        timestamp: new Date().toISOString(),
                        avatar: null,
                    }
                    : {
                        id: response.id,
                        content: response.content,
                        timestamp: new Date().toISOString(),
                        avatar: null,
                        timeTaken: response.metrics?.time ?? null,
                        tokenUsage: response.metrics?.usage ?? null,
                        intelligence: response.intelligence,
                    };
                const newChanges = !_.isString(response) && response.pendingChanges?.length
                    ? response.pendingChanges.map(a => ({
                        ...a,
                        status: a.status ?? 'pending',
                        createdAt: new Date().toISOString(),
                    }))
                    : [];
                const newQuestions = !_.isString(response) && response.pendingQuestions?.length
                    ? response.pendingQuestions.map(question => ({
                        ...question,
                        createdAt: new Date().toISOString(),
                    }))
                    : [];
                const shouldClearChanges = !_.isString(response) && response.clearPendingChanges;
                const shouldClearQuestions = !_.isString(response) && response.clearPendingQuestions;
                rs.updateState(s => {
                    const messageID = newMessage.id;
                    const currentLevel = s.messageCollapseLevels[messageID];
                    const shouldAutoCollapse = currentLevel === 1 || currentLevel === undefined;
                    const existingChanges = shouldClearChanges
                        ? s.pendingChanges.filter(a => a.status !== 'pending')
                        : s.pendingChanges;
                    const existingQuestions = shouldClearQuestions
                        ? []
                        : s.pendingQuestions;
                    return {
                        isRunning: false,
                        pendingMessage: null,
                        currentMessages: [...s.currentMessages, newMessage],
                        pendingChanges: [...existingChanges, ...newChanges],
                        pendingQuestions: _.chain([
                            ...existingQuestions,
                            ...newQuestions,
                        ])
                            .uniqBy(question => question.id)
                            .value(),
                        threadID: !_.isString(response) && response.threadID
                            ? response.threadID
                            : s.threadID,
                        messageCollapseLevels: shouldAutoCollapse
                            ? { ...s.messageCollapseLevels, [messageID]: 0 }
                            : s.messageCollapseLevels,
                    };
                });
            },
        });
    };
    const deleteControlledMessage = async (message, errorPrefix = 'Failed to delete message:') => (await rs.props.controlled?.onDeleteMessage?.(message)?.catch(error => {
        console.error(errorPrefix, error);
        return String(error);
    })) ?? 'Message deletion is unavailable';
    const handleDeleteMessage = (message) => {
        PromptConfirm({
            title: 'Delete message?',
            msg: 'This will permanently delete this message. This action cannot be undone.',
            size: [320, 190],
            yes: async (done) => {
                if (isControlled && rs.props.controlled) {
                    const deleteResult = await deleteControlledMessage(message);
                    done(deleteResult);
                    return;
                }
                rs.updateState(s => ({
                    currentMessages: s.currentMessages.filter(m => m.id !== message.id),
                }));
                done(true);
            },
        });
    };
    const canDeleteMessages = Boolean(rs.props.controlled?.onDeleteMessage);
    const handleRerunMessage = (message) => {
        PromptConfirm({
            title: 'Rerun response?',
            msg: 'This will delete this assistant response and any later user messages, then rerun the response.',
            size: [420, 200],
            yes: async (done) => {
                if (!isControlled || !rs.props.controlled) {
                    done(true);
                    return;
                }
                const currentMessages = rs.props.controlled.messages;
                const selectedAssistantIndex = currentMessages.findIndex(candidate => candidate.id === message.id);
                const latestAssistantIndex = _.findLastIndex(currentMessages, candidate => (candidate.content[0]?.role ?? 'assistant') === 'assistant');
                if (selectedAssistantIndex < 0 ||
                    selectedAssistantIndex !== latestAssistantIndex) {
                    Alerts.Alert({
                        msg: 'Only the latest assistant response can be rerun.',
                    });
                    done(true);
                    return;
                }
                const retryMessage = _.findLast(currentMessages.slice(0, selectedAssistantIndex), candidate => (candidate.content[0]?.role ?? 'assistant') === 'user');
                if (!retryMessage) {
                    Alerts.Alert({
                        msg: 'Could not find the user message to rerun.',
                    });
                    done(true);
                    return;
                }
                window.requestAnimationFrame(() => {
                    rs.props.controlled?.onRetryMessage?.(retryMessage);
                });
                done(true);
            },
        });
    };
    const canRerunMessages = Boolean(!effectiveIsRunning &&
        rs.props.controlled?.onDeleteMessage &&
        rs.props.controlled?.onRetryMessage);
    const effectiveTheme = rs.props.theme ?? 'light';
    const isDarkMode = effectiveTheme === 'dark';
    const effectiveThoughtAccess = rs.props.thoughtAccess ?? 'full';
    const updatePendingChanges = (updater) => {
        if (isControlled && rs.props.controlled) {
            rs.props.controlled.onPendingChangesChange(updater(rs.props.controlled.pendingChanges));
        }
        else {
            rs.updateState(s => ({
                pendingChanges: updater(s.pendingChanges),
            }));
        }
    };
    const updatePendingQuestions = (updater) => {
        if (isControlled && rs.props.controlled) {
            rs.props.controlled.onPendingQuestionsChange(updater(rs.props.controlled.pendingQuestions));
        }
        else {
            rs.updateState(s => ({
                pendingQuestions: updater(s.pendingQuestions),
            }));
        }
    };
    const handleSubmitQuestions = (answers) => {
        if (answers.length === 0) {
            return;
        }
        if (isControlled && rs.props.controlled) {
            rs.props.controlled.onSubmitPendingQuestions({ answers });
            return;
        }
        if (!effectiveThreadID || !rs.props.question) {
            return;
        }
        rs.updateState({
            isRunning: true,
            pendingMessage: {
                id: guid(),
                content: [],
                startedInference: null,
                currentStatus: 'Starting...',
            },
        });
        rs.props.question.onSubmit({
            threadID: effectiveThreadID,
            answers,
            onUpdate: progress => {
                rs.updateState({
                    pendingMessage: {
                        id: progress.id,
                        content: progress.content,
                        startedInference: progress.metrics?.timers?.startedInference ?? null,
                        currentStatus: progress.currentStatus,
                    },
                });
            },
            onComplete: result => {
                if (_.isString(result)) {
                    Alerts.Alert({ msg: result });
                }
                rs.updateState({ isRunning: false, pendingMessage: null });
                if (!_.isString(result) && result) {
                    const incomingQuestions = result.pendingQuestions ?? [];
                    updatePendingQuestions(() => incomingQuestions.map(question => ({
                        ...question,
                        createdAt: new Date().toISOString(),
                    })));
                }
            },
        });
    };
    const executeApprovedChanges = async (approvalIDs) => {
        if (!rs.props.change || !effectiveThreadID) {
            return;
        }
        if (approvalIDs.length === 0) {
            return;
        }
        const approvalSet = new Set(approvalIDs);
        updatePendingChanges(changes => changes.map(a => approvalSet.has(a.id) && a.status === 'pending'
            ? { ...a, status: 'executing' }
            : a));
        try {
            const result = await rs.props.change.onExecuteApproved({
                threadIDs: effectiveThreadID ? [effectiveThreadID] : [],
                approvalIDs: approvalIDs,
            });
            if (result.errors && result.errors.length > 0) {
                const errorMsg = result.errors
                    .map(e => `${e.toolName}: ${e.error}`)
                    .join('\n');
                const errorsByID = new Map(result.errors.map(error => [
                    error.approvalID,
                    `${error.toolName}: ${error.error}`,
                ]));
                updatePendingChanges(changes => changes.map(a => approvalSet.has(a.id) && a.status === 'executing'
                    ? {
                        ...a,
                        status: 'pending',
                        errorMessage: errorsByID.get(a.id) ?? undefined,
                    }
                    : a));
                Alerts.Alert({
                    msg: `Some changes failed to execute:\n${errorMsg}\n\nAll changes have been rolled back. You can reject the failed ones and retry.`,
                });
                return;
            }
            updatePendingChanges(changes => changes.map(a => approvalSet.has(a.id) && a.status === 'executing'
                ? { ...a, status: 'approved' }
                : a));
        }
        catch (e) {
            const message = e instanceof Error ? e.message : String(e);
            updatePendingChanges(changes => changes.map(a => approvalSet.has(a.id) && a.status === 'executing'
                ? {
                    ...a,
                    status: 'pending',
                    errorMessage: message,
                }
                : a));
            Alerts.Alert({ msg: `Change execution failed: ${message}` });
        }
    };
    const rejectPendingChanges = (approvalIDs) => {
        if (approvalIDs.length === 0) {
            return;
        }
        const approvalSet = new Set(approvalIDs);
        updatePendingChanges(changes => changes.map(a => approvalSet.has(a.id) && a.status === 'pending'
            ? { ...a, status: 'rejected' }
            : a));
        rs.props.change?.onReject?.(approvalIDs).catch(console.error);
    };
    const handleApproveChanges = async (approvalIDs) => {
        const approvalSet = new Set(approvalIDs);
        const pendingIDs = _.chain(effectivePendingChanges)
            .filter(a => approvalSet.has(a.id) && a.status === 'pending')
            .map(a => a.id)
            .value();
        await executeApprovedChanges(pendingIDs);
    };
    const handleRejectChanges = (approvalIDs) => {
        const approvalSet = new Set(approvalIDs);
        const pendingIDs = _.chain(effectivePendingChanges)
            .filter(a => approvalSet.has(a.id) && a.status === 'pending')
            .map(a => a.id)
            .value();
        rejectPendingChanges(pendingIDs);
    };
    const showChangesTray = !rs.props.hideTrays &&
        rs.props.change &&
        effectivePendingChanges.some(a => a.status === 'pending' ||
            a.status === 'executing' ||
            a.status === 'error' ||
            (Boolean(rs.props.showApprovedChanges) && a.status === 'approved'));
    const showQuestionsTray = !rs.props.hideTrays &&
        !effectiveIsRunning &&
        effectivePendingQuestions.some(question => question.status === 'pending');
    const executingChangeIDs = React.useMemo(() => new Set(effectivePendingChanges
        .filter(a => a.status === 'executing')
        .map(a => a.id)), [effectivePendingChanges]);
    const setTrayMode = React.useCallback((tray, mode) => {
        rs.updateState(state => {
            const nextState = tray === 'changes'
                ? { changesTrayMode: mode }
                : { questionsTrayMode: mode };
            if (mode === 'expanded') {
                if (tray === 'changes') {
                    nextState.questionsTrayMode = 'collapsed';
                }
                else {
                    nextState.changesTrayMode = 'collapsed';
                }
            }
            else if (state.changesTrayMode === 'expanded' ||
                state.questionsTrayMode === 'expanded') {
                if (tray === 'changes') {
                    nextState.questionsTrayMode =
                        state.questionsTrayMode === 'collapsed'
                            ? 'open'
                            : state.questionsTrayMode;
                }
                else {
                    nextState.changesTrayMode =
                        state.changesTrayMode === 'collapsed'
                            ? 'open'
                            : state.changesTrayMode;
                }
            }
            return nextState;
        });
    }, [rs]);
    React.useEffect(() => {
        if (!showChangesTray && rs.state.changesTrayMode !== 'open') {
            rs.updateState({ changesTrayMode: 'open' });
        }
    }, [rs, rs.state.changesTrayMode, showChangesTray]);
    React.useEffect(() => {
        if (!showQuestionsTray && rs.state.questionsTrayMode !== 'open') {
            rs.updateState({ questionsTrayMode: 'open' });
        }
    }, [rs, rs.state.questionsTrayMode, showQuestionsTray]);
    const expandedTray = showChangesTray && rs.state.changesTrayMode === 'expanded'
        ? 'changes'
        : showQuestionsTray && rs.state.questionsTrayMode === 'expanded'
            ? 'questions'
            : null;
    const handleDroppedFiles = async (items) => {
        await Sleep(0);
        const dt = new DataTransfer();
        const files = _.chain(items)
            .map(item => {
            if (!item.Data) {
                return null;
            }
            const bytes = Uint8Array.from(atob(item.Data), c => c.charCodeAt(0));
            const mimeType = item.MimeType ?? inferMimeTypeFromExtension(item.Filename);
            return new File([bytes], item.Filename, { type: mimeType });
        })
            .compact()
            .value();
        _.map(files, file => dt.items.add(file));
        if (dt.files.length > 0) {
            rs.updateState({ droppedFiles: dt.files });
        }
    };
    const inferMimeTypeFromExtension = (filename) => {
        const ext = filename.split('.').pop()?.toLowerCase() ?? '';
        const mimeMap = {
            pdf: 'application/pdf',
            png: 'image/png',
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            gif: 'image/gif',
            webp: 'image/webp',
            bmp: 'image/bmp',
            xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            xls: 'application/vnd.ms-excel',
        };
        return mimeMap[ext] ?? 'application/octet-stream';
    };
    return (React.createElement(FileUploadDropZone, { multiple: true, disabled: effectiveIsRunning, skipStorageServer: true, draggingOverMessage: "Drop files to attach", onUpload: handleDroppedFiles, className: "h-full" },
        React.createElement("div", { ref: rs.refs.container, className: BuildClass({
                'flex flex-col h-full relative': true,
                'bg-[oklch(var(--chat-l-bg)_0_0)]': true,
            }), style: {
                ...getChatCSSVars(rs.props.hue, isDarkMode),
                fontFamily: 'var(--font-condensed)',
            } },
            React.createElement("div", { className: "flex-1 relative min-h-0" },
                React.createElement("div", { className: "absolute inset-0 pointer-events-none z-0", style: {
                        backgroundColor: isDarkMode
                            ? 'oklch(0 0 0 / 0.9)'
                            : 'oklch(0 0 0 / 0.1)',
                    } }),
                React.createElement("div", { className: "absolute inset-0 pointer-events-none z-0", style: {
                        backgroundImage: 'url(/static/img/svg/window-background-texture.svg)',
                        backgroundRepeat: 'repeat',
                        backgroundSize: '5px',
                        filter: isDarkMode
                            ? 'hue-rotate(200deg) invert(1) brightness(0.9)'
                            : 'hue-rotate(260deg) grayscale(50%)',
                        opacity: isDarkMode ? 0.15 : 0.2,
                    } }),
                React.createElement("div", { ref: rs.refs.messageList, className: "absolute inset-0 overflow-y-scroll z-10 flex flex-col px-1", onWheel: handleScrollIntent, onClick: e => {
                        const target = e.target;
                        if (!target.closest('button, a, input, textarea, [role="button"]')) {
                            rs.refs.textarea.current?.focus();
                        }
                    } },
                    expandedTray == null && (React.createElement("div", { className: BuildClass({
                            'w-full max-w-[800px] mx-auto pt-4': true,
                            'flex-1 flex': Boolean(rs.props.messagesSlot),
                        }) },
                        rs.props.messagesSlot ?? (React.createElement(ChatThreadTurns, { rs: rs, messages: effectiveMessages, thoughtAccess: effectiveThoughtAccess, theme: effectiveTheme, onDeleteMessage: canDeleteMessages
                                ? handleDeleteMessage
                                : undefined, onRerunMessage: canRerunMessages
                                ? handleRerunMessage
                                : undefined, toolHandlers: toolHandlers })),
                        React.createElement(ChatThreadStreamingAndChanges, { rs: rs, pendingMessage: effectivePendingMessage, thoughtAccess: effectiveThoughtAccess, theme: effectiveTheme, showChangesTray: false, showQuestionsTray: false, showApprovedChanges: Boolean(rs.props.showApprovedChanges), pendingChanges: effectivePendingChanges, pendingQuestions: effectivePendingQuestions, changesTrayMode: rs.state.changesTrayMode, questionsTrayMode: rs.state.questionsTrayMode, onChangesTrayModeChange: mode => {
                                setTrayMode('changes', mode);
                            }, onQuestionsTrayModeChange: mode => {
                                setTrayMode('questions', mode);
                            }, executingChangeIDs: executingChangeIDs, isRunning: Boolean(effectiveIsRunning), onApproveChanges: handleApproveChanges, onRejectChanges: handleRejectChanges, onSubmitQuestions: handleSubmitQuestions }),
                        hints && (React.createElement("div", { className: "mt-4 px-2" },
                            React.createElement("div", { className: "flex flex-wrap gap-2" }, hints.map((hint, i) => (React.createElement("button", { key: i, type: "button", onClick: () => {
                                    handleSend([
                                        {
                                            role: 'user',
                                            timestamp: new Date().toISOString(),
                                            type: 'text',
                                            text: hint.text,
                                        },
                                    ], rs.state.input
                                        .intelligence);
                                }, className: BuildClass({
                                    'p-1 rounded-full text-sm transition-all': true,
                                    'bg-white/80 border border-neutral-200 text-neutral-600 hover:bg-white hover:border-neutral-300': !isDarkMode,
                                    'bg-neutral-800/80 border border-neutral-700 text-neutral-400 hover:bg-neutral-800 hover:border-neutral-600': isDarkMode,
                                }) }, hint.text)))))))),
                    expandedTray == null ? (React.createElement(ChatThreadStreamingAndChanges, { rs: rs, pendingMessage: null, thoughtAccess: effectiveThoughtAccess, theme: effectiveTheme, showChangesTray: Boolean(showChangesTray), showQuestionsTray: Boolean(showQuestionsTray), showApprovedChanges: Boolean(rs.props.showApprovedChanges), pendingChanges: effectivePendingChanges, pendingQuestions: effectivePendingQuestions, changesTrayMode: rs.state.changesTrayMode, questionsTrayMode: rs.state.questionsTrayMode, onChangesTrayModeChange: mode => {
                            setTrayMode('changes', mode);
                        }, onQuestionsTrayModeChange: mode => {
                            setTrayMode('questions', mode);
                        }, executingChangeIDs: executingChangeIDs, isRunning: Boolean(effectiveIsRunning), onQuestionTabChange: () => {
                            if (rs.state.isScrollAnchored &&
                                !userScrolledAwayFromBottomRef.current) {
                                window.requestAnimationFrame(() => {
                                    scrollToBottom();
                                });
                            }
                        }, onApproveChanges: handleApproveChanges, onRejectChanges: handleRejectChanges, onSubmitQuestions: handleSubmitQuestions })) : (React.createElement("div", { className: "mt-3 min-h-full flex flex-col px-1" },
                        React.createElement(ChatThreadStreamingAndChanges, { rs: rs, pendingMessage: null, thoughtAccess: effectiveThoughtAccess, theme: effectiveTheme, showChangesTray: expandedTray === 'changes', showQuestionsTray: expandedTray === 'questions', showApprovedChanges: Boolean(rs.props.showApprovedChanges), pendingChanges: effectivePendingChanges, pendingQuestions: effectivePendingQuestions, changesTrayMode: rs.state.changesTrayMode, questionsTrayMode: rs.state.questionsTrayMode, onChangesTrayModeChange: mode => {
                                setTrayMode('changes', mode);
                            }, onQuestionsTrayModeChange: mode => {
                                setTrayMode('questions', mode);
                            }, executingChangeIDs: executingChangeIDs, isRunning: Boolean(effectiveIsRunning), onQuestionTabChange: () => {
                                if (rs.state.isScrollAnchored &&
                                    !userScrolledAwayFromBottomRef.current) {
                                    window.requestAnimationFrame(() => {
                                        scrollToBottom();
                                    });
                                }
                            }, onApproveChanges: handleApproveChanges, onRejectChanges: handleRejectChanges, onSubmitQuestions: handleSubmitQuestions }))),
                    expandedTray == null && (React.createElement("div", { style: { height: rs.state.inputHeight + 20 } })),
                    React.createElement(CJSX, { cond: expandedTray == null &&
                            (Boolean(rs.props.sending) ||
                                Boolean(rs.props.controlled)) &&
                            !Boolean(rs.props.isReadOnly) },
                        React.createElement("div", { ref: rs.refs.inputWrapper, className: "sticky bottom-0 mt-auto py-2 z-20", onClick: e => {
                                const target = e.target;
                                if (!target.closest('button, input, textarea')) {
                                    rs.refs.textarea.current?.focus();
                                }
                            } },
                            React.createElement("div", { className: "w-full max-w-[800px] mx-auto" },
                                rs.props.textboxSlotAbove && (React.createElement("div", { className: "px-2 py-1" }, rs.props.textboxSlotAbove)),
                                React.createElement(ThreadTextBox, { value: rs.state.input, onUpdate: input => {
                                        rs.updateState({ input });
                                    }, onSend: handleSend, autoSendOnMount: rs.props.autoSendOnMount, disabled: effectiveIsRunning, placeholder: rs.props.placeholder, textareaRef: rs.refs.textarea, darkMode: isDarkMode, showFloatShadow: !rs.state.isScrollAnchored, externalFiles: rs.state.droppedFiles, contextReadTokens: rs.props.contextReadTokens, allowMaximumIntelligence: rs.props.allowMaximumIntelligence }),
                                rs.props.textboxSlotBelow && (React.createElement("div", { className: "px-2 py-1" }, rs.props.textboxSlotBelow))))),
                    React.createElement(CJSX, { cond: expandedTray == null && Boolean(rs.props.isReadOnly) },
                        React.createElement("div", { className: "sticky bottom-0 mt-auto py-3 z-20" },
                            React.createElement("div", { className: "w-full max-w-[800px] mx-auto px-4" },
                                React.createElement("div", { className: BuildClass({
                                        'rounded-lg px-4 py-3 text-sm': true,
                                        'bg-neutral-100 text-neutral-600 border border-neutral-200': !isDarkMode,
                                        'bg-neutral-800 text-neutral-400 border border-neutral-700': isDarkMode,
                                    }) }, rs.props.readOnlyMessage ??
                                    'This thread is read-only.')))))))));
});
const getDefaultState = (p) => {
    const baseInput = createEmptyInputState();
    const seeded = p.originalInputState ?? {};
    return {
        input: {
            ...baseInput,
            ...seeded,
            text: seeded.text ?? p.originalMessageState ?? '',
            voice: seeded.voice ?? baseInput.voice,
            attachments: seeded.attachments ?? baseInput.attachments,
        },
        currentMessages: p.messages,
        pendingMessage: null,
        isRunning: false,
        threadID: p.initialThreadID ?? null,
        executingChangeToolCallIDs: {},
        pendingChanges: _.map(p.initialPendingChanges, c => ({
            ...c,
            status: c.status ?? 'pending',
            createdAt: new Date().toISOString(),
        })),
        pendingQuestions: _.map(p.initialPendingQuestions, question => ({
            ...question,
            createdAt: new Date().toISOString(),
        })),
        expandedThinkingMessageID: null,
        messageCollapseLevels: {},
        messageExpandedItems: {},
        inputHeight: 60,
        isScrollAnchored: true,
        droppedFiles: null,
        changesTrayMode: 'open',
        questionsTrayMode: 'open',
    };
};
export const getThreadFileLookup = (messages, pendingMessage) => _.chain([
    ...messages.flatMap(message => message.content),
    ...(pendingMessage?.content ?? []),
])
    .flatMap(block => {
    if (block.type === 'attachment-manifest') {
        return block.files.map(file => [
            file.id,
            { mimeType: file.mimeType, size: file.size },
        ]);
    }
    if (block.type === 'file' && block.file.id != null) {
        return [
            [
                `thread:${block.file.id}`,
                {
                    mimeType: block.file.mimeType,
                    size: block.file.size,
                },
            ],
        ];
    }
    return [];
})
    .fromPairs()
    .value();
const groupMessagesIntoTurns = (messages) => {
    const userIndices = messages
        .map((msg, i) => ((msg.content[0]?.role ?? 'assistant') === 'user' ? i : -1))
        .filter(i => i >= 0);
    if (userIndices.length === 0) {
        return [{ userMessage: null, responses: messages }];
    }
    const turns = [];
    const firstUserIdx = userIndices[0];
    if (firstUserIdx !== undefined && firstUserIdx > 0) {
        turns.push({
            userMessage: null,
            responses: messages.slice(0, firstUserIdx),
        });
    }
    userIndices.forEach((userIdx, i) => {
        const nextUserIdx = userIndices[i + 1] ?? messages.length;
        const userMsg = messages[userIdx];
        if (userMsg) {
            turns.push({
                userMessage: userMsg,
                responses: messages.slice(userIdx + 1, nextUserIdx),
            });
        }
    });
    return turns;
};
const UserMessageInTurn = (props) => {
    const { message, rs, thoughtAccess, onDeleteMessage } = props;
    return (React.createElement(ThreadMessage, { ...message, className: rs.props.messageClassName?.(message), bubbleClassName: rs.props.messageBubbleClassName?.(message), metadataSlot: rs.props.messageMetadataSlot?.(message), isExpanded: false, onToggleExpanded: () => { }, collapseLevel: 0, onCollapseLevelChange: () => { }, expandedItems: new Set(), onToggleItem: () => { }, toolRenderers: rs.props.toolRenderers, toolHandlers: rs.props.toolHandlers, groupChat: rs.props.groupChat, participantNames: rs.props.participantNames, currentUserID: rs.props.currentUserID, thoughtAccess: thoughtAccess, theme: rs.props.theme ?? 'light', hideAvatar: rs.props.hideUserAvatar, onDelete: onDeleteMessage
            ? () => {
                onDeleteMessage(message);
            }
            : undefined }));
};
const StreamingMessage = (props) => {
    const { pending, isExpanded, onToggleExpanded, collapseLevel, onCollapseLevelChange, expandedItems, onToggleItem, toolRenderers, toolHandlers, thoughtAccess, theme, } = props;
    const message = {
        id: pending.id,
        content: pending.content,
        timestamp: new Date().toISOString(),
        avatar: null,
        isStreaming: true,
        currentStatus: pending.currentStatus ?? 'Thinking...',
        intelligence: pending.intelligence,
    };
    return (React.createElement(ThreadMessage, { ...message, isExpanded: isExpanded, onToggleExpanded: onToggleExpanded, collapseLevel: collapseLevel, onCollapseLevelChange: onCollapseLevelChange, expandedItems: expandedItems, onToggleItem: onToggleItem, toolRenderers: toolRenderers, toolHandlers: toolHandlers, thoughtAccess: thoughtAccess, theme: theme }));
};
const getAsyncLoader = (renderer) => {
    if (!renderer || typeof renderer === 'function') {
        return null;
    }
    return renderer.asyncLoader ?? null;
};
const getLargerTrayAction = (displayMode) => {
    if (displayMode === 'open') {
        return { mode: 'expanded', title: 'Expand tray' };
    }
    if (displayMode === 'collapsed') {
        return { mode: 'expanded', title: 'Expand tray' };
    }
    return { mode: 'open', title: 'Restore tray to open' };
};
const getCollapseTrayAction = (displayMode) => {
    if (displayMode === 'open') {
        return { mode: 'collapsed', title: 'Collapse tray' };
    }
    if (displayMode === 'expanded') {
        return { mode: 'open', title: 'Restore tray to open' };
    }
    return { mode: 'open', title: 'Open tray' };
};
const TrayModeButtons = (props) => {
    const largerAction = getLargerTrayAction(props.displayMode);
    const collapseAction = getCollapseTrayAction(props.displayMode);
    return (React.createElement("div", { className: "flex items-center gap-1" },
        React.createElement(Button, { type: "borderless", title: largerAction.title, tailwind: true, className: "px-1 py-[2px] opacity-70 hover:opacity-100", onClick: () => {
                props.onChangeMode?.(largerAction.mode);
            } },
            React.createElement("img", { src: "/static/img/i8/ios16-expand.svg", className: "h-3 w-3 m-0 align-middle" })),
        React.createElement(Button, { type: "borderless", title: collapseAction.title, tailwind: true, className: "px-1 py-[2px] opacity-70 hover:opacity-100", onClick: () => {
                props.onChangeMode?.(collapseAction.mode);
            } },
            React.createElement("img", { src: "/static/img/i8/ios16-merge.svg", className: "h-3 w-3 m-0 align-middle" }))));
};
export const PendingChangesTray = (props) => {
    const { changes, renderers, onApprove, onReject, executingIDs, widgetToGroupCode } = props;
    const [viewMode, setViewMode] = React.useState('grouped');
    const [dataLoading, setDataLoadingState] = React.useState(false);
    const visible = React.useMemo(() => changes.filter(c => {
        if (c.status === 'pending' ||
            c.status === 'executing' ||
            c.status === 'error') {
            return true;
        }
        return Boolean(props.showApprovedChanges && c.status === 'approved');
    }), [changes, props.showApprovedChanges]);
    const visibleSignature = React.useMemo(() => _.chain(visible)
        .sortBy(change => change.id)
        .map(change => `${change.id}:${change.status}:${change.widget}`)
        .join('|')
        .value(), [visible]);
    const visibleRef = React.useRef(visible);
    React.useEffect(() => {
        visibleRef.current = visible;
    }, [visibleSignature]);
    const pendingIDs = _.chain(visible)
        .filter(change => change.status === 'pending')
        .map(change => change.id)
        .value();
    const pendingCount = pendingIDs.length;
    const displayMode = props.displayMode ?? 'open';
    React.useEffect(() => {
        let isCurrent = true;
        const currentVisible = visibleRef.current;
        const runLoaders = async () => {
            if (currentVisible.length === 0) {
                setDataLoadingState(false);
                return;
            }
            const loaders = _.chain(currentVisible)
                .groupBy(change => change.widget)
                .map((groupedChanges, widget) => ({
                changes: groupedChanges,
                loader: getAsyncLoader(props.renderers[widget]),
            }))
                .filter(entry => entry.loader != null)
                .value();
            if (loaders.length === 0) {
                setDataLoadingState(false);
                return;
            }
            setDataLoadingState(true);
            await Promise.all(loaders.map(entry => {
                if (!entry.loader) {
                    return Promise.resolve();
                }
                return entry.loader(entry.changes).catch(error => {
                    console.error(error);
                });
            }));
            if (isCurrent) {
                setDataLoadingState(false);
            }
        };
        runLoaders().catch(error => {
            console.error(error);
            if (isCurrent) {
                setDataLoadingState(false);
            }
        });
        return () => {
            isCurrent = false;
        };
    }, [props.renderers, visibleSignature]);
    if (visible.length === 0) {
        return React.createElement(React.Fragment, null);
    }
    const trayDisabled = props.disabled || dataLoading;
    if (displayMode === 'collapsed') {
        return (React.createElement("div", { className: "rounded-lg overflow-hidden bg-[oklch(var(--chat-l-surface)_0_0)] border border-[oklch(var(--chat-l-text)_0_0_/_0.15)]" },
            React.createElement("div", { className: "px-3 py-2 flex items-center justify-between gap-2" },
                React.createElement("span", { className: "text-sm font-medium text-[oklch(var(--chat-l-text)_0_0)]" },
                    pendingCount,
                    " pending change",
                    pendingCount !== 1 ? 's' : ''),
                props.onDisplayModeChange ? (React.createElement(TrayModeButtons, { displayMode: displayMode, onChangeMode: props.onDisplayModeChange })) : null)));
    }
    return (React.createElement("div", { className: "rounded-lg overflow-hidden bg-[oklch(var(--chat-l-surface)_0_0)] border border-[oklch(var(--chat-l-text)_0_0_/_0.15)]" },
        React.createElement("div", { className: "px-3 py-2 border-b border-[oklch(var(--chat-l-text)_0_0_/_0.1)] flex items-center justify-between" },
            React.createElement("div", { className: "flex items-center gap-3" },
                React.createElement("span", { className: "text-sm font-medium text-[oklch(var(--chat-l-text)_0_0)]" },
                    pendingCount,
                    " pending change",
                    pendingCount !== 1 ? 's' : ''),
                props.onDisplayModeChange ? (React.createElement(TrayModeButtons, { displayMode: displayMode, onChangeMode: props.onDisplayModeChange })) : null,
                React.createElement(ButtonGroup, { options: [
                        {
                            value: 'individual',
                            label: 'Individual',
                            disabled: trayDisabled,
                        },
                        {
                            value: 'grouped',
                            label: 'Grouped',
                            disabled: trayDisabled,
                        },
                    ], value: viewMode, onUpdate: value => {
                        setViewMode(value);
                    } })),
            React.createElement("div", { className: "flex gap-2" },
                React.createElement(Button, { type: "trans-green", lbl: "Approve All", disabled: trayDisabled || pendingCount === 0, doubleClickNeeded: true, timeoutConfirmingMs: 2500, className: "px-2", classNameSpan: "text-xs", onClick: () => {
                        onApprove(pendingIDs);
                    } }),
                React.createElement(Button, { type: "trans-red", lbl: "Reject All", disabled: trayDisabled || pendingCount === 0, doubleClickNeeded: true, timeoutConfirmingMs: 2500, className: "px-2", classNameSpan: "text-xs", onClick: () => {
                        onReject(pendingIDs);
                    } }))),
        React.createElement("div", null, viewMode === 'individual' ? (React.createElement("div", { className: "divide-y divide-[oklch(var(--chat-l-text)_0_0_/_0.05)]" }, visible.map(change => (React.createElement("div", { key: change.id, className: "p-3 bg-white/50" },
            React.createElement(PendingChangeItem, { change: change, renderer: renderers[change.widget], toolHandlers: props.toolHandlers, onApprove: () => {
                    onApprove([change.id]);
                }, onReject: () => {
                    onReject([change.id]);
                }, disabled: trayDisabled || executingIDs.has(change.id), status: change.status, showActions: change.status !== 'approved' })))))) : (React.createElement("div", { className: "divide-y divide-[oklch(var(--chat-l-text)_0_0_/_0.05)]" }, _.chain(visible)
            .groupBy(c => widgetToGroupCode[c.widget] ?? c.widget)
            .toPairs()
            .map(([groupKey, groupChanges]) => (React.createElement("div", { key: groupKey, className: "p-3 bg-white/50" },
            React.createElement(PendingChangeGroup, { groupKey: groupKey, changes: groupChanges, renderers: renderers, toolHandlers: props.toolHandlers, onApprove: onApprove, onReject: onReject, disabled: trayDisabled, executingIDs: executingIDs }))))
            .value())))));
};
const FIXED_QUESTION_OPTIONS = [
    {
        id: 'fixed-clarify',
        label: "I don't understand the question, please clarify",
    },
    { id: 'fixed-skip', label: 'Skip question' },
];
const MAX_MANUAL_QUESTION_ANSWER_LENGTH = 2000;
const QuestionToolIcon = () => (React.createElement("svg", { className: "h-[22px] w-[22px] shrink-0", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true" },
    React.createElement("circle", { cx: "12", cy: "12", r: "10", fill: "oklch(var(--chat-l-text) 0 0 / 0.08)", stroke: "oklch(var(--chat-l-text) 0 0 / 0.34)", strokeWidth: "1.4" }),
    React.createElement("path", { d: "M8.7 9.15C8.95 6.9 10.35 5.7 12.35 5.7C14.45 5.7 15.85 6.95 15.85 8.8C15.85 10.3 15.05 11.1 13.8 11.85C12.65 12.55 12.35 13.05 12.35 14.15V14.55", stroke: "oklch(var(--chat-l-text) 0 0 / 0.86)", strokeWidth: "1.85", strokeLinecap: "round", strokeLinejoin: "round" }),
    React.createElement("circle", { cx: "12.35", cy: "18", r: "1.15", fill: "oklch(var(--chat-l-text) 0 0 / 0.86)" })));
const PendingQuestionsTray = (props) => {
    const [activeTab, setActiveTab] = React.useState('');
    const [configuredSelections, setConfiguredSelections] = React.useState({});
    const [fixedSelections, setFixedSelections] = React.useState({});
    const [manualSelection, setManualSelection] = React.useState({});
    const [manualAnswers, setManualAnswers] = React.useState({});
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const scheduledTabMoveFrameRef = React.useRef(null);
    const allTabIDsRef = React.useRef([]);
    React.useEffect(() => {
        return () => {
            if (scheduledTabMoveFrameRef.current !== null) {
                window.cancelAnimationFrame(scheduledTabMoveFrameRef.current);
            }
        };
    }, []);
    React.useEffect(() => {
        setIsSubmitting(false);
    }, [props.disabled]);
    const pendingQuestions = props.questions.filter(question => question.status === 'pending');
    const getOptionID = (option) => `option:${option}`;
    const questionTabIDs = pendingQuestions.map(question => `question:${question.id}`);
    const allTabIDs = [...questionTabIDs, 'preview'];
    allTabIDsRef.current = allTabIDs;
    React.useEffect(() => {
        if (!activeTab || !allTabIDs.includes(activeTab)) {
            setActiveTab(allTabIDs[0] ?? 'preview');
        }
    }, [activeTab, allTabIDs]);
    React.useEffect(() => {
        if (!activeTab) {
            return;
        }
        props.onActiveTabChange?.();
    }, [activeTab, props.onActiveTabChange]);
    if (pendingQuestions.length === 0) {
        return React.createElement(React.Fragment, null);
    }
    const displayMode = props.displayMode ?? 'open';
    const activeTabIndex = Math.max(0, allTabIDs.indexOf(activeTab));
    const moveTabBy = (step) => {
        const nextIndex = Math.min(allTabIDs.length - 1, Math.max(0, activeTabIndex + step));
        const nextTab = allTabIDs[nextIndex];
        if (nextTab) {
            setActiveTab(nextTab);
        }
    };
    const scheduleTabMove = (step) => {
        if (scheduledTabMoveFrameRef.current !== null) {
            window.cancelAnimationFrame(scheduledTabMoveFrameRef.current);
        }
        scheduledTabMoveFrameRef.current = window.requestAnimationFrame(() => {
            scheduledTabMoveFrameRef.current = null;
            setActiveTab(currentTab => {
                const tabIDs = allTabIDsRef.current;
                const currentIndex = Math.max(0, tabIDs.indexOf(currentTab));
                const nextIndex = Math.min(tabIDs.length - 1, Math.max(0, currentIndex + step));
                return tabIDs[nextIndex] ?? currentTab;
            });
        });
    };
    const getQuestionTabLabel = (question) => {
        const title = question.title?.trim() ?? '';
        if (title.length > 0) {
            return title.slice(0, 25);
        }
        return _.truncate(question.question.trim(), { length: 25 });
    };
    const getConfiguredSelection = (questionID) => configuredSelections[questionID] ?? new Set();
    const getFixedSelection = (questionID) => fixedSelections[questionID] ?? null;
    const isManualSelected = (questionID) => Boolean(manualSelection[questionID]);
    const getManualAnswer = (questionID) => manualAnswers[questionID] ?? '';
    const handleConfiguredOptionSelection = (question, option, isSelected) => {
        if (!question.multiple && !isSelected) {
            return;
        }
        setConfiguredSelections(prev => {
            const optionID = getOptionID(option);
            const current = prev[question.id] ?? new Set();
            const next = new Set(current);
            if (question.multiple) {
                if (isSelected) {
                    next.add(optionID);
                }
                else {
                    next.delete(optionID);
                }
            }
            else {
                next.clear();
                if (isSelected) {
                    next.add(optionID);
                }
            }
            return { ...prev, [question.id]: next };
        });
        setFixedSelections(prev => ({ ...prev, [question.id]: null }));
        if (!question.multiple && isSelected) {
            setManualSelection(prev => ({ ...prev, [question.id]: false }));
        }
    };
    const handleFixedOptionSelection = (questionID, fixedOptionID, isSelected) => {
        if (!isSelected) {
            return;
        }
        setFixedSelections(prev => ({ ...prev, [questionID]: fixedOptionID }));
        setConfiguredSelections(prev => ({ ...prev, [questionID]: new Set() }));
        setManualSelection(prev => ({ ...prev, [questionID]: false }));
    };
    const handleManualAnswerChange = (question, answerText) => {
        setManualAnswers(prev => ({
            ...prev,
            [question.id]: answerText,
        }));
        if (answerText.length > 0) {
            setManualSelection(prev => ({
                ...prev,
                [question.id]: true,
            }));
            setFixedSelections(prev => ({ ...prev, [question.id]: null }));
            if (!question.multiple) {
                setConfiguredSelections(prev => ({
                    ...prev,
                    [question.id]: new Set(),
                }));
            }
        }
    };
    const handleManualSelectionUpdate = (question, isSelected) => {
        if (!question.multiple && !isSelected) {
            return;
        }
        setManualSelection(prev => ({
            ...prev,
            [question.id]: isSelected,
        }));
        if (isSelected) {
            setFixedSelections(prev => ({ ...prev, [question.id]: null }));
            if (!question.multiple) {
                setConfiguredSelections(prev => ({
                    ...prev,
                    [question.id]: new Set(),
                }));
            }
        }
    };
    const buildAnswerText = (question) => {
        const fixedSelection = getFixedSelection(question.id);
        if (fixedSelection) {
            return (FIXED_QUESTION_OPTIONS.find(option => option.id === fixedSelection)
                ?.label ?? '');
        }
        const configuredAnswers = question.options
            .filter(option => getConfiguredSelection(question.id).has(getOptionID(option)))
            .map(option => option.trim());
        const manualAnswer = isManualSelected(question.id)
            ? getManualAnswer(question.id).trim()
            : '';
        return _.chain([...configuredAnswers, manualAnswer])
            .filter(value => value.length > 0)
            .join('; ')
            .value();
    };
    const isComplete = (question) => {
        const fixedSelection = getFixedSelection(question.id);
        if (fixedSelection) {
            return true;
        }
        const hasConfiguredSelection = getConfiguredSelection(question.id).size > 0;
        if (isManualSelected(question.id)) {
            return getManualAnswer(question.id).trim().length > 0;
        }
        return hasConfiguredSelection;
    };
    const allComplete = pendingQuestions.every(question => isComplete(question));
    const getQuestionAnswerStatus = (question) => {
        const fixedSelection = getFixedSelection(question.id);
        if (fixedSelection === 'fixed-skip') {
            return 'skip';
        }
        if (fixedSelection === 'fixed-clarify') {
            return 'clarify';
        }
        if (isComplete(question)) {
            return 'answered';
        }
        return 'unanswered';
    };
    const getQuestionStatusEmoji = (questionStatus) => {
        if (questionStatus === 'answered') {
            return '✅';
        }
        if (questionStatus === 'skip') {
            return '⛔';
        }
        if (questionStatus === 'clarify') {
            return '❓';
        }
        return '⭕';
    };
    const submissionAnswers = pendingQuestions.map(question => ({
        questionID: question.id,
        questionText: question.question,
        answerText: buildAnswerText(question),
    }));
    const tabs = [
        ...pendingQuestions.map(question => ({
            value: `question:${question.id}`,
            label: getQuestionTabLabel(question),
            tooltip: question.title || question.question,
            content: () => {
                const selectedConfiguredOptions = getConfiguredSelection(question.id);
                const selectedFixedOption = getFixedSelection(question.id);
                const manualSelected = isManualSelected(question.id);
                return (React.createElement("div", { className: "p-3 bg-white/50" },
                    React.createElement("div", { className: "text-[14px] font-medium text-[oklch(var(--chat-l-text)_0_0)]" }, question.question),
                    question.options.length > 0 && (React.createElement("div", { className: "mt-2 grid gap-1" }, question.options.map(option => (React.createElement("div", { key: getOptionID(option), "data-pending-question-control": "true" },
                        React.createElement(Checkbox, { lbl: option, value: selectedConfiguredOptions.has(getOptionID(option)), cbStyle: question.multiple ? 'checkbox' : 'radio', classNameLabel: "text-[14px]", disabled: props.disabled, onUpdate: value => {
                                handleConfiguredOptionSelection(question, option, value);
                                if (!question.multiple && value) {
                                    scheduleTabMove(1);
                                }
                            } })))))),
                    React.createElement("div", { className: "mt-2 flex items-start gap-2", "data-pending-question-control": "true" },
                        React.createElement(Checkbox, { lbl: "", value: manualSelected, cbStyle: question.multiple ? 'checkbox' : 'radio', title: "Manual answer", disabled: props.disabled, onUpdate: value => {
                                handleManualSelectionUpdate(question, value);
                            } }),
                        React.createElement("div", { className: "flex-1 min-w-0" },
                            React.createElement(TextareaWithVoice, { className: "m-0", classNameTextarea: BuildClass({
                                    'rounded border border-neutral-300 px-2 py-1 text-[14px]': true,
                                    'text-[oklch(var(--chat-l-text)_0_0)]': manualSelected,
                                    'text-[oklch(var(--chat-l-text)_0_0_/_0.45)]': !manualSelected,
                                }), value: getManualAnswer(question.id), onUpdate: value => {
                                    handleManualAnswerChange(question, value);
                                }, prompt: null, maxLength: MAX_MANUAL_QUESTION_ANSWER_LENGTH, placeholder: "Type a custom answer...", disabled: props.disabled, onKeyDown: event => {
                                    if (event.key === 'Enter' &&
                                        !event.shiftKey &&
                                        !event.nativeEvent.isComposing) {
                                        event.preventDefault();
                                        scheduleTabMove(1);
                                    }
                                } }))),
                    React.createElement("div", { className: "mt-2 grid gap-1" }, FIXED_QUESTION_OPTIONS.map(option => (React.createElement("div", { key: option.id, "data-pending-question-control": "true" },
                        React.createElement(Checkbox, { lbl: option.label, value: selectedFixedOption === option.id, cbStyle: "radio", classNameLabel: "text-[14px]", disabled: props.disabled, onUpdate: value => {
                                handleFixedOptionSelection(question.id, option.id, value);
                                if (value) {
                                    scheduleTabMove(1);
                                }
                            } }))))),
                    React.createElement("div", { className: "mt-3 flex items-center justify-between" },
                        React.createElement(Button, { type: "borderless", lbl: "\u2190 Previous", disabled: props.disabled || activeTabIndex === 0, classNameSpan: "text-[12px]", onClick: () => {
                                moveTabBy(-1);
                            } }),
                        React.createElement(Button, { type: "borderless", lbl: "Next \u2192", disabled: props.disabled ||
                                activeTabIndex >= allTabIDs.length - 1, classNameSpan: "text-[12px]", onClick: () => {
                                moveTabBy(1);
                            } }))));
            },
        })),
        {
            value: 'preview',
            label: 'Preview',
            content: () => (React.createElement("div", { className: "p-3 bg-white/50" },
                React.createElement("div", { className: "text-[14px] font-medium text-[oklch(var(--chat-l-text)_0_0)]" }, "You can still edit answers before sending. Click \"Submit Answers\" when ready."),
                React.createElement("div", { className: "mt-3 grid gap-2" }, submissionAnswers.map(answer => (React.createElement("div", { key: answer.questionID, className: "text-[14px] text-neutral-800" },
                    React.createElement("span", { className: "font-semibold" }, "Q:"),
                    ' ',
                    pendingQuestions.find(question => question.id === answer.questionID)?.question ?? '',
                    ' ',
                    React.createElement("span", { className: "font-semibold" }, "A:"),
                    ' ',
                    answer.answerText || '-')))),
                React.createElement("div", { className: "mt-3 flex items-center justify-between" },
                    React.createElement(Button, { type: "borderless", lbl: "\u2190 Previous", disabled: props.disabled || activeTabIndex === 0, classNameSpan: "text-[12px]", onClick: () => {
                            moveTabBy(-1);
                        } }),
                    React.createElement(Button, { type: "borderless", lbl: "Next \u2192", disabled: true, classNameSpan: "text-[12px]" })))),
        },
    ];
    const activeTabValue = activeTab || allTabIDs[0] || 'preview';
    const activeTabConfig = tabs.find(tab => tab.value === activeTabValue) ?? tabs[tabs.length - 1];
    if (displayMode === 'collapsed') {
        return (React.createElement("div", { className: "rounded-lg overflow-hidden bg-[oklch(var(--chat-l-surface)_0_0)] border border-[oklch(var(--chat-l-text)_0_0_/_0.15)]" },
            React.createElement("div", { className: "px-3 py-2 flex items-center justify-between gap-2" },
                React.createElement("div", { className: "flex min-w-0 items-center gap-2" },
                    React.createElement(QuestionToolIcon, null),
                    React.createElement("span", { className: "text-sm font-medium text-[oklch(var(--chat-l-text)_0_0)]" },
                        pendingQuestions.length,
                        " pending question",
                        pendingQuestions.length !== 1 ? 's' : '')),
                props.onDisplayModeChange ? (React.createElement(TrayModeButtons, { displayMode: displayMode, onChangeMode: props.onDisplayModeChange })) : null)));
    }
    return (React.createElement("div", { className: "rounded-lg overflow-hidden bg-[oklch(var(--chat-l-surface)_0_0)] border border-[oklch(var(--chat-l-text)_0_0_/_0.15)]" },
        React.createElement("div", { className: "px-3 py-2 min-h-[40px] border-b border-[oklch(var(--chat-l-text)_0_0_/_0.1)] grid grid-cols-[auto_1fr_auto] items-center gap-2" },
            React.createElement("div", { className: "flex items-center gap-2 shrink-0" },
                React.createElement(QuestionToolIcon, null),
                React.createElement("span", { className: "text-sm font-medium text-[oklch(var(--chat-l-text)_0_0)]" }, "Pending questions"),
                props.onDisplayModeChange ? (React.createElement(TrayModeButtons, { displayMode: displayMode, onChangeMode: props.onDisplayModeChange })) : null),
            React.createElement("div", { className: "flex min-w-0 justify-center" },
                React.createElement("div", { className: "flex items-center gap-x-1 gap-y-0.5" }, pendingQuestions.map((question, index) => {
                    const questionTabID = `question:${question.id}`;
                    const isActive = activeTabValue === questionTabID;
                    const questionStatus = getQuestionAnswerStatus(question);
                    return (React.createElement("button", { key: questionTabID, type: "button", title: question.title || question.question, className: BuildClass({
                            'inline-flex items-center gap-1 px-1.5 py-1 border-b-2 text-[14px] leading-[18px] min-w-0': true,
                            'border-[oklch(var(--chat-l-text)_0_0_/_0.6)] text-[oklch(var(--chat-l-text)_0_0)] font-medium': isActive,
                            'border-transparent text-[oklch(var(--chat-l-text)_0_0_/_0.7)] hover:text-[oklch(var(--chat-l-text)_0_0)] hover:border-[oklch(var(--chat-l-text)_0_0_/_0.25)]': !isActive,
                        }), onClick: () => {
                            setActiveTab(questionTabID);
                        } },
                        React.createElement("span", null,
                            "Q",
                            index + 1),
                        React.createElement("span", { className: "text-[13px] leading-none text-[oklch(var(--chat-l-text)_0_0_/_0.75)]" }, getQuestionStatusEmoji(questionStatus))));
                }))),
            React.createElement(Button, { type: "trans-blue", lbl: "Submit Answers", disabled: props.disabled || isSubmitting || pendingQuestions.length === 0, className: "px-2", classNameSpan: "text-[14px]", onClick: () => {
                    if (props.disabled || isSubmitting) {
                        return;
                    }
                    if (!allComplete) {
                        Alerts.Alert({
                            msg: 'Please complete all required answers',
                        });
                        return;
                    }
                    setIsSubmitting(true);
                    Promise.resolve(props.onSubmit(submissionAnswers))
                        .catch(error => {
                        console.error(error);
                    })
                        .finally(() => {
                        setIsSubmitting(false);
                    });
                } })),
        React.createElement("div", null,
            React.createElement("div", { className: "flex w-full flex-wrap items-stretch gap-1 border-b border-[oklch(var(--chat-l-text)_0_0_/_0.1)] px-2 py-1" }, tabs.map(tab => {
                const isActive = tab.value === activeTabValue;
                const isPreviewTab = tab.value === 'preview';
                return (React.createElement("button", { key: tab.value, type: "button", title: ('tooltip' in tab ? tab.tooltip : null) ?? tab.label, className: BuildClass({
                        'cursor-pointer min-w-0 flex-1 px-2 py-1 rounded border text-[14px] leading-[18px]': true,
                        'basis-[120px]': isPreviewTab,
                        'basis-[180px]': !isPreviewTab,
                        'border-[oklch(var(--chat-l-text)_0_0_/_0.2)] bg-white/80 text-[oklch(var(--chat-l-text)_0_0)] font-medium': isActive,
                        'border-transparent text-[oklch(var(--chat-l-text)_0_0_/_0.7)] hover:bg-white/40': !isActive,
                    }), onClick: () => {
                        setActiveTab(tab.value);
                    } },
                    React.createElement("span", { className: "block text-left leading-[16px] whitespace-normal break-words" }, tab.label)));
            })),
            React.createElement("div", { key: activeTabValue }, activeTabConfig?.content()))));
};
const PendingChangeItem = (props) => {
    const { change, renderer, toolHandlers, onApprove, onReject, disabled, status } = props;
    if (!renderer) {
        return React.createElement("div", { className: "text-xs text-red-500" },
            "No renderer for ",
            change.widget);
    }
    const rendererFn = typeof renderer === 'function' ? renderer : renderer.detail;
    return (React.createElement("div", { className: "flex gap-3" },
        React.createElement("div", { className: "flex-1 min-w-0" },
            rendererFn(change.input, toolHandlers),
            change.errorMessage && (React.createElement("div", { className: "mt-1 text-xs text-red-600 font-medium whitespace-pre-wrap" }, change.errorMessage))),
        React.createElement("div", { className: "flex flex-col gap-2 shrink-0 pt-0.5" }, props.showActions === false ? null : status === 'executing' ? (React.createElement("div", { className: "px-2 py-1" },
            React.createElement(LoadingSpinnerSmall, null))) : (React.createElement(React.Fragment, null,
            React.createElement(Button, { type: "trans-green", title: "Approve", disabled: disabled, doubleClickNeeded: true, timeoutConfirmingMs: 2000, tailwind: true, className: "px-1 py-[2px]", classNameSpan: "text-[10px] leading-[10px]", onClick: onApprove },
                React.createElement("img", { src: "/static/img/i8/ios16-tick.svg", className: "h-3 w-3 m-0 align-middle" })),
            React.createElement(Button, { type: "trans-red", title: "Reject", disabled: disabled, doubleClickNeeded: true, timeoutConfirmingMs: 2000, tailwind: true, className: "px-1 py-[2px]", classNameSpan: "text-[10px] leading-[10px]", onClick: onReject },
                React.createElement("img", { src: "/static/img/i8/ios16-cross.svg", className: "h-3 w-3 m-0 align-middle" })))))));
};
const getGroupRenderer = (changes, renderers) => {
    const tableRenderer = _.find(changes, change => {
        const renderer = renderers[change.widget];
        return (renderer != null && typeof renderer !== 'function' && renderer.table != null);
    });
    if (tableRenderer) {
        return renderers[tableRenderer.widget];
    }
    const fallbackRenderer = _.find(changes, change => renderers[change.widget] != null);
    if (!fallbackRenderer) {
        return undefined;
    }
    return renderers[fallbackRenderer.widget];
};
const PendingChangeGroup = (props) => {
    const { groupKey, changes, renderers, toolHandlers, onApprove, onReject, disabled, executingIDs, } = props;
    const groupLabel = _.startCase(groupKey.replace(/[-_]/g, ' '));
    const pendingIDs = _.chain(changes)
        .filter(change => change.status === 'pending')
        .map(change => change.id)
        .value();
    const pendingCount = pendingIDs.length;
    const renderer = getGroupRenderer(changes, renderers);
    const header = (React.createElement("div", { className: "mb-2 flex items-center justify-between" },
        React.createElement("div", { className: "text-xs font-semibold text-[oklch(var(--chat-l-text)_0_0)]" },
            groupLabel,
            pendingCount > 0 ? ` (${pendingCount})` : ''),
        React.createElement("div", { className: "flex gap-2" }, props.showGroupActions === false || pendingCount === 0 ? null : (React.createElement(React.Fragment, null,
            React.createElement(Button, { type: "trans-green", lbl: "Approve", disabled: disabled || pendingCount === 0, doubleClickNeeded: true, timeoutConfirmingMs: 2000, className: "px-2", classNameSpan: "text-xs", onClick: () => {
                    onApprove(pendingIDs);
                } }),
            React.createElement(Button, { type: "trans-red", lbl: "Reject", disabled: disabled || pendingCount === 0, doubleClickNeeded: true, timeoutConfirmingMs: 2000, className: "px-2", classNameSpan: "text-xs", onClick: () => {
                    onReject(pendingIDs);
                } }))))));
    if (!renderer || typeof renderer === 'function' || !renderer.table) {
        return (React.createElement("div", { className: "divide-y divide-neutral-100" },
            header,
            changes.map(c => (React.createElement("div", { key: c.id, className: "py-2 first:pt-0 last:pb-0" },
                React.createElement(PendingChangeItem, { change: c, renderer: renderers[c.widget], toolHandlers: toolHandlers, onApprove: () => {
                        onApprove([c.id]);
                    }, onReject: () => {
                        onReject([c.id]);
                    }, disabled: disabled || executingIDs.has(c.id), status: c.status, showActions: c.status !== 'approved' }))))));
    }
    const items = changes.map(c => ({
        id: c.id,
        input: c.input,
        status: c.status,
        errorMessage: c.errorMessage,
    }));
    const TableRenderer = renderer.table;
    return (React.createElement("div", null,
        header,
        React.createElement("div", { className: "overflow-x-auto" },
            React.createElement(TableRenderer, { items: items, handlers: toolHandlers, onApprove: id => {
                    onApprove([id]);
                }, onReject: id => {
                    onReject([id]);
                }, disabled: disabled }))));
};
//# sourceMappingURL=thread-conversation.js.map